import { useEffect, useState, useMemo } from "react";
import type { FC } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import SvgIcon from "@mui/material/SvgIcon";
import CheckCircleSolidIcon from "src/icons/untitled-ui/duocolor/check-circle-solid";
import useHousehold from "src/contexts/household-context";
import { usePeopleRolesStore } from "src/hooks/snug/use-peopleRoles";
import type { SpouseRelationshipTypes, UserDataObj } from "src/types/snugtotal";
import { LoadingButton } from "@mui/lab";
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import InputAdornment from "@mui/material/InputAdornment";
import { useNavigate } from 'react-router-dom';
import { paths } from "src/paths";

interface HouseholdContextDialogProps {
  onClose?: () => void;
  open?: boolean;
}

const stateSummaries: Record<string, string> = {
  AL: `Alabama is a common law property state. Probate is typically 6 to 12 months if over $25,000 in assets. There are no state estate or inheritance taxes.`,
  AK: `Alaska is a opt-in community property state. Probate is typically 6 to 12 months if over $100k vehicles and $50k other property in assets. There are no state estate or inheritance taxes.`,
  AZ: `Arizona is a community property state. Probate is typically 9 to 18 months if over $75k personal and $100k real estate in assets. There are no state estate or inheritance taxes.`,
  AR: `Arkansas is a common law property state. Probate is typically 6 to 12 months if over $100,000 in assets. There are no state estate or inheritance taxes.`,
  CA: `California is a community property state. Probate is typically 12 to 24 months if over $184,500 in assets. There are no state estate or inheritance taxes.`,
  CO: `Colorado is a common law property state. Probate is typically 6 to 12 months if over $82,000 in assets. There are no state estate or inheritance taxes.`,
  CT: `Connecticut is a common law property state. Probate is typically 9 to 18 months if over $40,000 in assets. There are state estate taxes of 12% over $13.99M.`,
  DE: `Delaware is a common law property state. Probate is typically 9 to 12 months if over $30,000 in assets. There are no state estate or inheritance taxes.`,
  DC: `District of Columbia is a common law property state. Probate is typically 9 to 18 months if over $40,000 in assets. There are state estate taxes of up to 16% over $4.873M.`,
  FL: `Florida is a common law property state. Probate is typically 9 to 18 months if over $75,000 in assets. There are no state estate or inheritance taxes.`,
  GA: `Georgia is a common law property state. Probate is typically 6 to 12 months if over $10,000 in assets. There are no state estate or inheritance taxes.`,
  HI: `Hawaii is a common law property state. Probate is typically 9 to 18 months if over $100,000 (vehicles excluded) in assets. There are state estate taxes of up to 20% over $5.49 M.`,
  ID: `Idaho is a community property state. Probate is typically 6 to 12 months if over $100,000 in assets. There are no state estate or inheritance taxes.`,
  IL: `Illinois is a common law property state. Probate is typically 9 to 18 months if over $100,000 in assets. There are state estate taxes of up to 16% over $4 M.`,
  IN: `Indiana is a common law property state. Probate is typically 6 to 12 months if over $50,000 in assets. There are no state estate or inheritance taxes.`,
  IA: `Iowa is a common law property state. Probate is typically 9 to 12 months if over $200k in assets. There are no state estate or inheritance taxes.`,
  KS: `Kansas is a common law property state. Probate is typically 6 to 12 months if over $75,000 in assets. There are no state estate or inheritance taxes.`,
  KY: `Kentucky is a common law property state. Probate is typically 6 to 12 months if over $15,000 in assets. There are state inheritance taxes of up to 16% over $500, depending on heir class.`,
  LA: `Louisiana is a community property state. Probate is typically 9 to 18 months if over $125,000 in assets. There are no state estate or inheritance taxes.`,
  ME: `Maine is a common law property state. Probate is typically 6 to 12 months if over $40,000 in assets. There are state estate taxes of up to 12% over $7M.`,
  MD: `Maryland is a common law property state. Probate is typically 9 to 18 months if over $50k in assets ($100k if all to spouse). There are state estate taxes of up to 16% over $5M and inheritance taxes of 10% on non-exempt heirs over $1k.`,
  MA: `Massachusetts is a common law property state. Probate is typically 9 to 12 months if over $25,000 in assets and vehicle. There are state estate taxes of up to 16% over $2M.`,
  MI: `Michigan is a common law property state. Probate is typically 6 to 12 months if over $25,000 in assets and allowances. There are no state estate or inheritance taxes.`,
  MN: `Minnesota is a common law property state. Probate is typically 6 to 12 months if over $75,000 in assets. There are state estate taxes of up to 16% over $3M.`,
  MS: `Mississippi is a common law property state. Probate is typically 9 to 12 months if over $75,000 in assets. There are no state estate or inheritance taxes.`,
  MO: `Missouri is a common law property state. Probate is typically 6 to 12 months if over $40,000 in assets. There are no state estate or inheritance taxes.`,
  MT: `Montana is a common law property state. Probate is typically 6 to 12 months if over $50,000 in assets. There are no state estate or inheritance taxes.`,
  NE: `Nebraska is a common law property state. Probate is typically 6 to 12 months if over $100k in assets. There are state inheritance taxes of up to 15% over $25k, depending on heir class.`,
  NV: `Nevada is a community property state. Probate is typically 9 to 12 months if over $25k in assets ($100k if all to spouse). There are no state estate or inheritance taxes.`,
  NH: `New Hampshire is a common law property state. Probate is typically 9 to 12 months if over $10,000 in assets. There are no state estate or inheritance taxes.`,
  NJ: `New Jersey is a common law property state. Probate is typically 9 to 18 months if over $20k in assets ($50k if all to spouse). There are state inheritance taxes of up to 16% over $25k, depending on heir class.`,
  NM: `New Mexico is a community property state. Probate is typically 6 to 12 months if over $50,000 in assets. There are no state estate or inheritance taxes.`,
  NY: `New York is a common law property state. Probate is typically 9 to 24 months if over $50,000 in assets. There are state estate taxes of up to 16% over $7.16M.`,
  NC: `North Carolina is a common law property state. Probate is typically 6 to 12 months if over $20k in assets ($30k if all to spouse). There are no state estate or inheritance taxes.`,
  ND: `North Dakota is a common law property state. Probate is typically 6 to 12 months if over $50,000 in assets. There are no state estate or inheritance taxes.`,
  OH: `Ohio is a common law property state. Probate is typically 9 to 12 months if over $35k in assets ($100k if all to spouse). There are no state estate or inheritance taxes.`,
  OK: `Oklahoma is a common law property state. Probate is typically 6 to 12 months if over $200k in assets. There are no state estate or inheritance taxes.`,
  OR: `Oregon is a common law property state. Probate is typically 9 to 18 months if over $75k in personal assets and $200k in real estate. There are state estate taxes of up to 16% over $1M.`,
  PA: `Pennsylvania is a common law property state. Probate is typically 9 to 18 months if over $50,000 in assets. There are state inheritance taxes of up to 15%, depending on heir class.`,
  RI: `Rhode Island is a common law property state. Probate is typically 9 to 12 months if over $15,000 in assets. There are state estate taxes of up to 16% over $1.802M.`,
  SC: `South Carolina is a common law property state. Probate is typically 9 to 12 months if over $25,000 in assets. There are no state estate or inheritance taxes.`,
  SD: `South Dakota is a common law property state. Probate is typically 6 to 12 months if over $50,000 in assets. There are no state estate or inheritance taxes.`,
  TN: `Tennessee is a common law property state. Probate is typically 6 to 12 months if over $50,000 in assets. There are no state estate or inheritance taxes.`,
  TX: `Texas is a community property state. Probate is typically 6 to 12 months if over $75,000 in assets. There are no state estate or inheritance taxes.`,
  UT: `Utah is a common law property state. Probate is typically 6 to 12 months if over $100,000 in assets. There are no state estate or inheritance taxes.`,
  VT: `Vermont is a common law property state. Probate is typically 9 to 12 months if over $45k in assets and no real estate. There are state estate taxes of up to 16% over $5M.`,
  VA: `Virginia is a common law property state. Probate is typically 6 to 12 months if over $50,000 in assets. There are no state estate or inheritance taxes.`,
  WA: `Washington is a community property state. Probate is typically 9 to 12 months if over $100,000 in assets. There are state estate taxes of up to 20% over $2.193M.`,
  WV: `West Virginia is a common law property state. Probate is typically 6 to 12 months if over $50k in personal assets and $100k in real estate. There are no state estate or inheritance taxes.`,
  WI: `Wisconsin is a community property state. Probate is typically 9 to 12 months if over $50,000 in assets. There are no state estate or inheritance taxes.`,
  WY: `Wyoming is a common law property state. Probate is typically 6 to 12 months if over $200,000 in assets. There are no state estate or inheritance taxes.`
};

const probateThresholds: Record<string, string> = {
  AL: `Under $25,000`,
  AK: `Under $50,000`,
  AZ: `Under $175,000`,
  AR: `Under $100,000`,
  CA: `Under $184,000`,
  CO: `Under $82,000`,
  CT: `Under $40,000`,
  DE: `Under $30,000`,
  DC: `Under $40,000`,
  FL: `Under $75,000`,
  GA: `Under $10,000`,
  HI: `Under $100,000`,
  ID: `Under $100,000`,
  IL: `Under $100,000`,
  IN: `Under $50,000`,
  IA: `Under $200,000`,
  KS: `Under $75,000`,
  KY: `Under $15,000`,
  LA: `Under $125,000`,
  ME: `Under $40,000`,
  MD: `Under $50,000`,
  MA: `Under $25,000`,
  MI: `Under $25,000`,
  MN: `Under $75,000`,
  MS: `Under $75,000`,
  MO: `Under $40,000`,
  MT: `Under $50,000`,
  NE: `Under $100,000`,
  NV: `Under $25,000`,
  NH: `Under $10,000`,
  NJ: `Under $20,000`,
  NM: `Under $50,000`,
  NY: `Under $50,000`,
  NC: `Under $20,000`,
  ND: `Under $50,000`,
  OH: `Under $35,000`,
  OK: `Under $200,000`,
  OR: `Under $275,000`,
  PA: `Under $50,000`,
  RI: `Under $15,000`,
  SC: `Under $25,000`,
  SD: `Under $50,000`,
  TN: `Under $50,000`,
  TX: `Under $75,000`,
  UT: `Under $100,000`,
  VT: `Under $45,000`,
  VA: `Under $50,000`,
  WA: `Under $100,000`,
  WV: `Under $150,000`,
  WI: `Under $50,000`,
  WY: `Under $200,000`,
};

const probateThresholdSeconds: Record<string, string> = {
  AL: `$25k - $1M`,
  AK: `$50k - $1M`,
  AZ: `$175k - $1M`,
  AR: `$100k - $1M`,
  CA: `$184k - $1M`,
  CO: `$82k - $1M`,
  CT: `$40k - $1M`,
  DE: `$30k - $1M`,
  DC: `$40k - $1M`,
  FL: `$75k - $1M`,
  GA: `$10k - $1M`,
  HI: `$100k - $1M`,
  ID: `$100k - $1M`,
  IL: `$100k - $1M`,
  IN: `$50k - $1M`,
  IA: `$200k - $1M`,
  KS: `$75k - $1M`,
  KY: `$15k - $1M`,
  LA: `$125k - $1M`,
  ME: `$40k - $1M`,
  MD: `$50k - $1M`,
  MA: `$25k - $1M`,
  MI: `$25k - $1M`,
  MN: `$75k - $1M`,
  MS: `$75k - $1M`,
  MO: `$40k - $1M`,
  MT: `$50k - $1M`,
  NE: `$100k - $1M`,
  NV: `$25k - $1M`,
  NH: `$10k - $1M`,
  NJ: `$20k - $1M`,
  NM: `$50k - $1M`,
  NY: `$50k - $1M`,
  NC: `$20k - $1M`,
  ND: `$50k - $1M`,
  OH: `$35k - $1M`,
  OK: `$200k - $1M`,
  OR: `$275k - $1M`,
  PA: `$50k - $1M`,
  RI: `$15k - $1M`,
  SC: `$25k - $1M`,
  SD: `$50k - $1M`,
  TN: `$50k - $1M`,
  TX: `$75k - $1M`,
  UT: `$100k - $1M`,
  VT: `$45k - $1M`,
  VA: `$50k - $1M`,
  WA: `$100k - $1M`,
  WV: `$150k - $1M`,
  WI: `$50k - $1M`,
  WY: `$200k - $1M`,
};

type StateCode = keyof typeof probateThresholds;

/** Build the two state-specific net-worth labels on demand */
const getNetWorthOptions = (state?: StateCode) => [
  { label: '', value: null },
  {
    label: state && probateThresholds[state]
      ? `${probateThresholds[state]} (exempt)`
      : 'Under threshold (exempt)',
    value: 'exempt'
  },
  {
    label: state && probateThresholdSeconds[state]
      ? probateThresholdSeconds[state]
      : '$25k – $1M',
    value: 'up_to_one'
  },
  { label: '$1M – $5M',  value: 'up_to_five' },
  { label: '$5M – $10M', value: 'up_to_ten' },
  { label: 'Over $10M',  value: 'over_ten' }
] as const;
export const maritalStatusOptions = [
  { label: "", value: null },
  { label: "Single", value: "single" },
  { label: "Married", value: "married" },
  { label: "Domestic Partnership", value: "domestic_partnership" },
  { label: "Civil Union", value: "civil_union" },
];
export const childrenOptions = [
  { label: "", value: null },
  { label: "No children", value: "none" },
  { label: "All children over 18", value: "adult" },
  { label: "Some/all children under 18", value: "minor" },
];
export const blendedFamilyOptions = [
  { label: "", value: null },
  { label: "Yes, we are a blended family", value: true },
  { label: "No, all children are ours together", value: false },
];
export const estatePlanFoundationOptions = [
  { label: "", value: null },
  { label: "Upload Attorney Documents", value: "attorney" },
  { label: "Joint Revocable Living Trust", value: "joint" },
  { label: "Individual Revocable Living Trust", value: "trust" },
  { label: "Last Will & Testament", value: "will" },
];
export const statesOptions = [
  { label: "", value: null },
  { label: "Alabama", value: "AL" },
  { label: "Alaska", value: "AK" },
  { label: "Arizona", value: "AZ" },
  { label: "Arkansas", value: "AR" },
  { label: "California", value: "CA" },
  { label: "Colorado", value: "CO" },
  { label: "Connecticut", value: "CT" },
  { label: "Delaware", value: "DE" },
  { label: "Florida", value: "FL" },
  { label: "Georgia", value: "GA" },
  { label: "Hawaii", value: "HI" },
  { label: "Idaho", value: "ID" },
  { label: "Illinois", value: "IL" },
  { label: "Indiana", value: "IN" },
  { label: "Iowa", value: "IA" },
  { label: "Kansas", value: "KS" },
  { label: "Kentucky", value: "KY" },
  { label: "Louisiana", value: "LA" },
  { label: "Maine", value: "ME" },
  { label: "Maryland", value: "MD" },
  { label: "Massachusetts", value: "MA" },
  { label: "Michigan", value: "MI" },
  { label: "Minnesota", value: "MN" },
  { label: "Mississippi", value: "MS" },
  { label: "Missouri", value: "MO" },
  { label: "Montana", value: "MT" },
  { label: "Nebraska", value: "NE" },
  { label: "Nevada", value: "NV" },
  { label: "New Hampshire", value: "NH" },
  { label: "New Jersey", value: "NJ" },
  { label: "New Mexico", value: "NM" },
  { label: "New York", value: "NY" },
  { label: "North Carolina", value: "NC" },
  { label: "North Dakota", value: "ND" },
  { label: "Ohio", value: "OH" },
  { label: "Oklahoma", value: "OK" },
  { label: "Oregon", value: "OR" },
  { label: "Pennsylvania", value: "PA" },
  { label: "Rhode Island", value: "RI" },
  { label: "South Carolina", value: "SC" },
  { label: "South Dakota", value: "SD" },
  { label: "Tennessee", value: "TN" },
  { label: "Texas", value: "TX" },
  { label: "Utah", value: "UT" },
  { label: "Vermont", value: "VT" },
  { label: "Virginia", value: "VA" },
  { label: "Washington", value: "WA" },
  { label: "West Virginia", value: "WV" },
  { label: "Wisconsin", value: "WI" },
  { label: "Wyoming", value: "WY" },
  { label: "District of Columbia", value: "DC" },
];

export const HouseholdContextDialog: FC<HouseholdContextDialogProps> = (
  props
) => {
  const {
    onClose,
    open = false,
    ...other
  } = props;

  const { selectedHousehold, onboardingHandleEditHoHUserData, refreshHouseholds } = useHousehold();
  const hOH = selectedHousehold?.head_of_household;

  // Local state for all fields
  const [maritalStatus, setMaritalStatus] = useState<string>("");
  const [children, setChildren] = useState<string>(hOH?.children || "");
  const [blendedFamily, setBlendedFamily] = useState<boolean | null>(hOH?.blended_family ?? null);
  const [netWorth, setNetWorth] = useState<string>(hOH?.value_of_assets || "");
  const [state, setState] = useState<string>(hOH?.household_state_code || "");
  const [estatePlanFoundation, setEstatePlanFoundation] = useState<string>(hOH?.estate_plan_foundation || "");
  const [spouseNameError, setSpouseNameError] = useState<string>("");
  const [saving, setSaving] = useState(false);

  const netWorthOptions = useMemo(
    () => getNetWorthOptions(state as StateCode | undefined),
    [state]
  );

  useEffect(() => {
    setNetWorth(hOH?.value_of_assets || "");
  }, [hOH?.value_of_assets]);

  useEffect(() => {
    setChildren(hOH?.children || "");
    setBlendedFamily(hOH?.blended_family ?? null);
    setState(hOH?.household_state_code || "");
    setEstatePlanFoundation(hOH?.estate_plan_foundation || "");
  }, [hOH]);

  // Clear blended family when children is set to "none"
  useEffect(() => {
    if (children === "none") {
      setBlendedFamily(null);
    }
  }, [children]);

  // PeopleRoles store for spouse save
  const { peopleRoles, targetUserDatas, handleAddOrUpdatePeopleRolesAndUserData } = usePeopleRolesStore(hOH?.ud_id || null);
  const spouse_role = peopleRoles?.find((role: any) => role.role === 'SPOUSE');
  const [spouseName, setSpouseName] = useState<string>("");
  const [spouseEmail, setSpouseEmail] = useState<string>("");
  const [spouseEmailError, setSpouseEmailError] = useState<string>("");
  const [targetUserData, setTargetUserData] = useState<UserDataObj | null>(null);
  useEffect(() => {
    setTargetUserData(
      targetUserDatas?.find(
        (d) => d.ud_id === spouse_role?.role_target_user_data_ud_id
      ) || null
    );
  }, [spouse_role, targetUserDatas]);

  // Prepopulate spouse fields if spouse PeopleRole exists
  useEffect(() => {
    if (spouse_role && targetUserData) {
      setSpouseName(targetUserData.full_name || "");
      setSpouseEmail(targetUserData.contact_email || "");
      setMaritalStatus(spouse_role?.spouse_relationship || "");
    }
  }, [spouse_role, targetUserData]);

  const [summary, setSummary] = useState<string>(() => stateSummaries['CA']);
  useEffect(() => {
    setSummary(
      stateSummaries[state as keyof typeof stateSummaries] ?? ''
    );
  }, [state]);

  // props or data you already have
  // const allowWills   = selectedHousehold?.professional_group?.allow_wills;   // global
  // const allowTrusts  = selectedHousehold?.professional_group?.allow_trusts;  // global
  // const blockWill    = selectedHousehold?.household_role?.block_will;         // per-client
  // const blockTrust   = selectedHousehold?.household_role?.block_trust;        // per-client

  // ✨ handy one-liners ----------------------------------------------------------
  // const canWill  = allowWills  && !blockWill;   // true → this client may create a Will
  // const canTrust = allowTrusts && !blockTrust;  // true → this client may create a Trust

  const navigate = useNavigate();

  // helper: map stored value → label
  const estatePlanLabel =
  estatePlanFoundationOptions.find(o => o.value === estatePlanFoundation)?.label ?? "";

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      open={open}
      {...other}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Box sx={{ flexGrow: 1, p: 4 }}>
          <Stack spacing={6}>
            <Stack spacing={3}>
              <Typography variant="h5">Family Snapshot</Typography>
              <Typography variant="body2">
                Update your Family Snapshot to get personalized recommendations on documents and tasks for you and your family's estate planning.
              </Typography>
              <TextField
                fullWidth
                label="Marital Status"
                name="marital_status"
                required
                select
                InputProps={{ sx: { color: "black" } }}
                onChange={(e) => setMaritalStatus(e.target.value as string)}
                SelectProps={{ native: true }}
                value={maritalStatus || ""}
              >
                {maritalStatusOptions.map((option) => (
                  <option key={option.value} value={option.value || ""}>
                    {option.label}
                  </option>
                ))}
              </TextField>
              {/* Spouse fields if not single */}
              {maritalStatus && maritalStatus !== 'single' && (
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label="Spouse Name"
                    name="spouse_name"
                    value={spouseName}
                    onChange={e => {
                      setSpouseName(e.target.value);
                      setSpouseNameError("");
                    }}
                    error={!!spouseNameError}
                    helperText={spouseNameError}
                  />
                  <TextField
                    fullWidth
                    label="Spouse Email"
                    name="spouse_email"
                    type="email"
                    required
                    value={spouseEmail}
                    onChange={e => {
                      setSpouseEmail(e.target.value);
                      setSpouseEmailError("");
                    }}
                    onBlur={e => {
                      const value = e.target.value.trim();
                      if (!value) {
                        setSpouseEmailError("Spouse email is required");
                      } else if (!/^\S+@\S+\.\S+$/.test(value)) {
                        setSpouseEmailError("Please enter a valid email address");
                      } else {
                        setSpouseEmailError("");
                      }
                    }}
                    error={!!spouseEmailError}
                    helperText={spouseEmailError}
                  />
                </Stack>
              )}
              <TextField
                fullWidth
                label="Children"
                name="children"
                required
                select
                InputProps={{ sx: { color: "black" } }}
                onChange={(e) => setChildren(e.target.value as string)}
                SelectProps={{ native: true }}
                value={children || ""}
              >
                {childrenOptions.map((option) => (
                  <option key={option.value} value={option.value || ""}>
                    {option.label}
                  </option>
                ))}
              </TextField>
              {children && children !== "none" && (
                <TextField
                  fullWidth
                  label="Blended Family"
                  name="blended_family"
                  required
                  select
                  InputProps={{ sx: { color: "black" } }}
                  onChange={(e) => setBlendedFamily(e.target.value === 'true')}
                  SelectProps={{ native: true }}
                  value={blendedFamily !== null ? String(blendedFamily) : ""}
                >
                  {blendedFamilyOptions.map((option) => (
                    <option key={String(option.value)} value={option.value !== null ? String(option.value) : ''}>
                      {option.label}
                    </option>
                  ))}
                </TextField>
              )}
              <TextField
                fullWidth
                label="State of Residence"
                name="state"
                required
                select
                InputProps={{ sx: { color: "black" } }}
                onChange={(e) => setState(e.target.value as string)}
                SelectProps={{ native: true }}
                value={state || ""}
              >
                {statesOptions.map((option) => (
                  <option key={option.value} value={option.value || ""}>
                    {option.label}
                  </option>
                ))}
              </TextField>
              <TextField
                fullWidth
                label="Net Worth"
                name="net_worth"
                required
                select
                InputProps={{ sx: { color: "black" } }}
                onChange={(e) => setNetWorth(e.target.value as string)}
                SelectProps={{ native: true }}
                value={netWorth || ""}
              >
                {netWorthOptions.map((option) => (
                  <option key={option.value} value={option.value || ""}>
                    {option.label}
                  </option>
                ))}
              </TextField>
              
              <TextField
                fullWidth
                label="Estate Plan Foundation"
                value={estatePlanLabel}          // shows “Estate Planning Attorney”, etc.
                InputProps={{
                  readOnly: true,                // no caret, no editing
                  sx: { color: "black" },
                  endAdornment: (
                    <InputAdornment position="end" sx={{ mr: -1.5 }}>
                      <Button
                        onClick={() => navigate(paths.dashboard.onboarding)}
                        size="small"
                        sx={{ textTransform: "none" }}
                        startIcon={<SwapHorizRoundedIcon />}
                      >
                        Change plan
                      </Button>
                    </InputAdornment>
                  )
                }}
              />

              <Card
                variant="outlined"
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  // border: "none",
                  backgroundColor: "#FAF9F8",
                }}
              >
                <CardContent
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    p: 2,
                    '&:last-child': {
                      paddingBottom: 3,
                    },
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems={'center'}>
                    <SvgIcon fontSize="large" sx={{ color: "#3e8a28" }}>
                      <CheckCircleSolidIcon />
                    </SvgIcon>
                    <Stack direction="column" spacing={1}>

                      <Typography
                        sx={{ mt: 1, flex: 1 }}
                        variant="body2"
                      >
                        <b>We have you covered!</b> {summary}
                      </Typography>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>


              <Stack
                alignItems="center"
                direction="row"
                flexWrap="wrap"
                spacing={2}
                justifyContent="space-between"
              >
                <Stack
                  alignItems="center"
                  direction="row"
                  flexWrap="wrap"
                  spacing={2}
                >
                  <LoadingButton
                    variant="contained"
                    loading={saving}
                    disabled={saving || Boolean(maritalStatus && maritalStatus !== 'single' && (spouseEmailError || spouseNameError))}
                    onClick={async () => {
                      setSaving(true);
                      try {
                        // Handle spouse data based on marital status
                        if (maritalStatus && maritalStatus !== 'single') {
                          // Validate spouse fields if not single
                          let hasError = false;
                          if (!spouseName) {
                            setSpouseNameError("Spouse name is required");
                            hasError = true;
                          } else {
                            setSpouseNameError("");
                          }
                          if (!spouseEmail) {
                            setSpouseEmailError("Spouse email is required");
                            hasError = true;
                          } else if (!/^\S+@\S+\.\S+$/.test(spouseEmail)) {
                            setSpouseEmailError("Please enter a valid email address");
                            hasError = true;
                          } else {
                            setSpouseEmailError("");
                          }
                          if (hasError) {
                            setSaving(false);
                            return;
                          }
                          // Add/update spouse data
                          await handleAddOrUpdatePeopleRolesAndUserData(hOH?.ud_id || null, {
                            id: spouse_role?.id || null,
                            role: 'SPOUSE',
                            spouse_relationship: (["married", "domestic_partnership", "civil_union"].includes(maritalStatus)
                              ? (maritalStatus as SpouseRelationshipTypes)
                              : undefined),
                            is_household_member: true,
                            role_target_user_data: {
                              ud_id: targetUserData?.ud_id || null,
                              full_name: spouseName,
                              contact_email: spouseEmail,
                            },
                          });
                        } else if (maritalStatus === 'single' && spouse_role?.id) {
                          // Update existing spouse role to "OTHER" instead of deleting
                          await handleAddOrUpdatePeopleRolesAndUserData(hOH?.ud_id || null, {
                            id: spouse_role.id,
                            role: 'OTHER', // Change from SPOUSE to OTHER
                            spouse_relationship: undefined, // Remove spousal relationship
                            is_household_member: true,
                            role_target_user_data: {
                              ud_id: targetUserData?.ud_id || null,
                              full_name: targetUserData?.full_name || "",
                              contact_email: targetUserData?.contact_email || "",
                            },
                          });
                        }
                        await onboardingHandleEditHoHUserData(hOH?.ud_id || "", {
                          value_of_assets: netWorth,
                          estate_plan_foundation: estatePlanFoundation,
                          children: children,
                          blended_family: children && children !== "none" ? blendedFamily : null,
                          household_state_code: state,
                        });
                        await refreshHouseholds();
                        if (onClose) onClose();
                      } finally {
                        setSaving(false);
                      }
                    }}
                  >
                    Save and continue
                  </LoadingButton>
                  <Button
                    color="inherit"
                    onClick={onClose}
                  >
                    Cancel
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Dialog>
  );
};

HouseholdContextDialog.propTypes = {
  onClose: PropTypes.func,
  open: PropTypes.bool,
};
